import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {unstable_batchedUpdates} from 'react-dom'
import {Box} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import dayjs from 'dayjs'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import EmptyState from 'src/components-new/layout/EmptyState'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import {
  AssumptionTableColumn,
  formatAssumptionTime,
  formatBodyParts,
  formatSecondaryAssumptionState,
  getAssumptionColumnOptions,
  getAssumptionTableColumns,
  getSecondaryAssumptionKey,
  getSecondaryAssumptionStateTone,
  isDerivedSecondaryAssumption,
  isSecondaryAssumptionAnswered,
  isUpcomingSecondaryAssumption,
  SECONDARY_ASSUMPTION_STATE_LABELS,
  SecondaryAssumption,
} from 'src/constants/secondaryAssumptions'
import apiClient from 'src/services/apiClient'
import AssumptionResponseDialogNew from 'src/pages-new/secondaryAssumptions/AssumptionResponseDialogNew'
import AsNeededAssumptionDialogNew from 'src/pages-new/secondaryAssumptions/AsNeededAssumptionDialogNew'
import NextDoseDialogNew from 'src/pages-new/secondaryAssumptions/NextDoseDialogNew'
import QuickAnswerButtonsNew from 'src/pages-new/secondaryAssumptions/QuickAnswerButtonsNew'
import NextDosesCard from './NextDosesCard'
import useFlyToHistory from './useFlyToHistory'

interface SecondaryAssumptionsPanelProps {
  secondaryTherapies: Record<string, any>[]
  // Pre-selects "Terapia" when arriving from elsewhere already knowing which one matters — e.g.
  // the "Prossima assunzione" link on the deep-link response page (SecondaryAssumptionResponseNew),
  // which wants to land here already scoped to that same therapy instead of making the patient
  // re-pick it. No equivalent for "Stato": arriving here should show this therapy's assumptions
  // unfiltered, same as picking the tab manually.
  initialTherapyId?: string
  // Read-only history, as the doctor sees it in the patient detail's "Diario" tab: no
  // answer/edit actions column, no "Prossima assunzione" card, no response dialogs. The therapies
  // passed in are then the patient's own (apiClient.getPatientsSecondaryTherapies), not the logged
  // user's.
  readOnly?: boolean
  // therapy-service specs/2026-10-as-needed-assumptions.md: a dose "al bisogno" may create the
  // patient's "al bisogno" therapy for that drug — the parent reloads `secondaryTherapies` then.
  onSecondaryTherapiesChanged?: () => void
}

// "Terapia" filter option: every accessory therapy of the patient with the same drug name, so
// picking "Voltaren" shows the reminders of all of them (psp-backend merges them, see
// getSecondaryAssumptions). A therapy without a drug name is never merged with anything.
interface TherapyGroup {
  key: string
  label: string
  therapyIds: string[]
}

function groupTherapiesByDrug(therapies: Record<string, any>[]): TherapyGroup[] {
  const groups = new Map<string, TherapyGroup>()
  therapies.forEach((therapy) => {
    // Trimmed/case-insensitive key: "Voltaren" and "voltaren " are the same drug to the patient.
    const name: string | undefined = therapy.drug?.name?.trim() || undefined
    const key = name ? `drug:${name.toLowerCase()}` : `therapy:${therapy._id}`
    const group = groups.get(key) ?? {key, label: name ?? 'Terapia accessoria', therapyIds: []}
    group.therapyIds.push(therapy._id)
    groups.set(key, group)
  })
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label, 'it', {sensitivity: 'base'}))
}

type SecondaryAssumptionRow = SecondaryAssumption & {rowKey: string}

// "Now", kept current: re-renders on every minute boundary so a dose whose time comes while the
// page stays open moves from the "Prossima assunzione" card into the table on its own.
function useNow(): dayjs.Dayjs {
  const [now, setNow] = useState(() => dayjs())
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined
    const timeout = setTimeout(() => {
      setNow(dayjs())
      interval = setInterval(() => setNow(dayjs()), 60_000)
    }, 60_000 - (Date.now() % 60_000))
    return () => {
      clearTimeout(timeout)
      if (interval) clearInterval(interval)
    }
  }, [])
  return now
}

function formatDayLabel(date?: string): string {
  return date ? dayjs(date).locale('it').format('ddd DD MMMM YYYY') : '—'
}

/**
 * "Terapie Accessorie" tab of the new-style "Diario paziente" page
 * (src/pages-new/diary/DiaryNew.tsx). A table, same look as the "Terapie" tab
 * (PrimaryAssumptionsPanel): the "Filtra" button (same Filters convention as
 * PrimaryAssumptionsPanel) picks which accessory therapy to look at, and each reminder is a row
 * with Data/Ora/Farmaco/Stato columns (or the ones the PSP configured, see getAssumptionTableColumns) and, in place of PrimaryAssumptionsPanel's
 * visualizza/scarica actions (there is no "infusion sheet" for accessory therapies), round check/✗
 * quick-answer controls — an edit icon once a row is already answered, since correcting it needs
 * the full form (see AssumptionResponseDialogNew). The first row may be the RF1.4/RF3.1.1 derived
 * "next dose" (no `_id` yet, answered by natural key — see getSecondaryAssumptionKey).
 *
 * With `readOnly` the same table is the doctor's view in the patient detail's "Diario" tab
 * (src/pages-new/patient/tabs/DiarioTab.tsx): history only, nothing to answer.
 */
export default function SecondaryAssumptionsPanel({
  secondaryTherapies,
  initialTherapyId,
  readOnly = false,
  onSecondaryTherapiesChanged
}: SecondaryAssumptionsPanelProps): JSX.Element {
  const toast = useToast()
  const now = useNow()
  const [therapies, setTherapies] = useState<Record<string, any>[]>(secondaryTherapies)
  const therapyGroups = useMemo(() => groupTherapiesByDrug(therapies), [therapies])
  const groupKeyOf = (therapyId: string | undefined, groups: TherapyGroup[]): string | undefined =>
    groups.find((group) => therapyId !== undefined && group.therapyIds.includes(therapyId))?.key
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | undefined>(() => {
    const groups = groupTherapiesByDrug(secondaryTherapies)
    // No drug picked by default: the tab opens on every accessory therapy at once. Only a deep
    // link (initialTherapyId, e.g. "Prossima assunzione") lands already scoped to its drug.
    return groupKeyOf(initialTherapyId, groups)
  })
  const selectedGroup = therapyGroups.find((group) => group.key === selectedGroupKey)
  // The therapies the table covers: the picked drug's, or all of them when none is picked.
  const activeTherapyIds = useMemo(
    () => selectedGroup?.therapyIds ?? therapies.map((therapy) => therapy._id as string),
    [selectedGroup, therapies]
  )
  const [stateFilter, setStateFilter] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string | null>(null)
  const [dateTo, setDateTo] = useState<string | null>(null)

  const [assumptions, setAssumptions] = useState<SecondaryAssumption[]>([])
  // The derived next doses (RF3.1.1) only come with the first page, so they are kept from the
  // last first-page fetch: browsing to page 2 must not make the "Prossima assunzione" card vanish.
  // Every filter change goes back to the first page, so they never go stale with respect to them.
  const [nextDoses, setNextDoses] = useState<SecondaryAssumption[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>()
  const [statusCount, setStatusCount] = useState<string>()
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})
  const [denyingId, setDenyingId] = useState<string | undefined>(undefined)
  const [respondingTo, setRespondingTo] = useState<SecondaryAssumption | undefined>(undefined)
  const [confirmingTaken, setConfirmingTaken] = useState<SecondaryAssumption | undefined>(undefined)
  // the card's ✓/✗ open the same dialog, which can answer the dose or move it (NextDoseDialogNew)
  const [cardDialog, setCardDialog] = useState<{dose: SecondaryAssumption; presetTaken: 'yes' | 'no'} | undefined>(undefined)
  // therapy-service specs/2026-10-as-needed-assumptions.md: the "Assunzione al bisogno" button,
  // always there — any drug of the catalog, with or without a therapy for it yet
  const [asNeededOpen, setAsNeededOpen] = useState<boolean>(false)
  // PSP-configured columns (Diario paziente > Terapie accessorie > Colonne in the backoffice)
  const assumptionColumnOptions = useAppSelector(state => getAssumptionColumnOptions(state.pspConfigReducer.data))
  const tableRef = useRef<HTMLDivElement>(null)
  const {flight, launch: launchFlight, flightLayer} = useFlyToHistory(tableRef)

  useEffect(() => {
    setTherapies(secondaryTherapies)
    const groups = groupTherapiesByDrug(secondaryTherapies)
    if (selectedGroupKey && !groups.some((group) => group.key === selectedGroupKey)) {
      setSelectedGroupKey(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondaryTherapies])

  // `silent`: refresh after an answer without flipping the table to its loading state — the row
  // was already patched in place (see onAnswered), so a spinner would only make it flicker.
  const fetchAssumptions = useCallback((silent = false) => {
    const therapyIds = activeTherapyIds
    if (therapyIds.length === 0) return
    const noop = (): void => {}
    const countParams = {
      ...(therapyIds.length === 1 ? {secondaryTherapyId: therapyIds[0]} : {secondaryTherapyIds: therapyIds.join(',')}),
      state: stateFilter || undefined,
      dateFrom: dateFrom ?? undefined,
      dateTo: dateTo ?? undefined,
    }
    Promise.all([
      apiClient.getSecondaryAssumptions({
        ...countParams,
        _l: pagination.rowsPerPage,
        _sk: pagination.page * pagination.rowsPerPage,
      }, silent ? noop : setStatus),
      apiClient.getSecondaryAssumptionsCount(countParams, silent ? noop : setStatusCount),
    ])
      .then(([data, countResult]) => {
        const list: SecondaryAssumption[] = Array.isArray(data) ? data : []
        setAssumptions(list)
        if (pagination.page === 0) setNextDoses(list.filter(isDerivedSecondaryAssumption))
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => { if (!silent) setStatus(STATUS.REJECTED) })
  }, [activeTherapyIds, stateFilter, dateFrom, dateTo, pagination])

  useEffect(() => { fetchAssumptions() }, [fetchAssumptions])

  useEffect(() => {
    setPagination((prev) => ({...prev, page: 0}))
  }, [selectedGroupKey])

  const asNeededButton = !readOnly && (
    <Button
      variant="primary"
      startIcon={<AddRoundedIcon />}
      onClick={() => setAsNeededOpen(true)}
    >
      Assunzione al bisogno
    </Button>
  )

  // The table shows the dose just recorded: the "Terapia" filter is dropped when it is on another
  // drug, and the therapies are reloaded when the dose created its "al bisogno" therapy.
  const onAsNeededCreated = (created: SecondaryAssumption): void => {
    setAsNeededOpen(false)
    const createdName = created.drugName?.trim().toLowerCase()
    if (selectedGroup && createdName && selectedGroup.label.toLowerCase() !== createdName) {
      setSelectedGroupKey(undefined)
    }
    if (therapies.some((therapy) => therapy._id === created.secondaryTherapyId)) {
      fetchAssumptions(true)
    } else {
      onSecondaryTherapiesChanged?.()
    }
  }

  const asNeededDialog = !readOnly && (
    <AsNeededAssumptionDialogNew
      open={asNeededOpen}
      onClose={() => setAsNeededOpen(false)}
      onCreated={onAsNeededCreated}
    />
  )

  if (therapies.length === 0) {
    return (
      <Box>
        {asNeededButton && <Box sx={{mb: 2, display: 'flex', justifyContent: 'flex-end'}}>{asNeededButton}</Box>}
        <EmptyState title="Nessuna terapia accessoria trovata" />
        {asNeededDialog}
      </Box>
    )
  }

  // Answering patches the already-loaded page in place for immediate feedback. Matched against the key
  // the row had BEFORE the patch (`original`), not against `updated._id`: the derived row (no
  // `_id` yet, RF1.4) turns into a persisted one once answered, so its `_id` changes from null to
  // a real one and can no longer be used to find it back in the list.
  // Returns true when it cleared the "Stato" filter, which already triggers a refetch on its own.
  const patchLocal = (original: SecondaryAssumption, updated: SecondaryAssumption): boolean => {
    const key = getSecondaryAssumptionKey(original)
    const patch = (list: SecondaryAssumption[]): SecondaryAssumption[] =>
      list.map((a) => (getSecondaryAssumptionKey(a) === key ? {...a, ...updated} : a))
    setAssumptions(patch)
    setNextDoses(patch)

    // If a "Stato" filter is active and this answer just moved the row out of it (e.g. it was
    // PLANNED — the common case when arriving here via the "Prossima assunzione" link, which
    // pre-filters to PLANNED — and is now COMPLETED/CANCELLED), the row would otherwise vanish
    // right after being answered with no explanation. Clear the filter instead, so the patient
    // sees the answer they just gave alongside everything else.
    if (stateFilter && updated.state && updated.state !== stateFilter) {
      setStateFilter('')
      setPagination((prev) => ({...prev, page: 0}))
      return true
    }
    return false
  }

  // After any answer the page is also reloaded from the server: the PATCH response alone isn't
  // enough to show everything that changed (e.g. answering the derived "next dose" early makes
  // therapy-service derive the following one, RF1.4.2, which the card then shows).
  // A dose answered from the "Prossime assunzioni" card also flies down into the table (see
  // useFlyToHistory). Batched with the patch: the flight measures the page as it was before the
  // answer, and React 17 doesn't batch state updates made after an `await` on its own.
  const onAnswered = (original: SecondaryAssumption, updated?: SecondaryAssumption): void => {
    let refetchTriggered = false
    unstable_batchedUpdates(() => {
      if (updated) {
        const cardIndex = upcomingDoses.findIndex((dose) => getSecondaryAssumptionKey(dose) === getSecondaryAssumptionKey(original))
        if (cardIndex >= 0) launchFlight(original, cardIndex, {...original, ...updated}, drugNameOf(original))
        refetchTriggered = patchLocal(original, updated)
      }
    })
    if (!refetchTriggered) fetchAssumptions(true)
  }

  const onQuickDeny = async (assumption: SecondaryAssumption): Promise<void> => {
    const key = getSecondaryAssumptionKey(assumption)
    setDenyingId(key)
    try {
      // RF4.3: the derived "next dose" row (no _id yet) is addressed by natural key instead.
      const updated = assumption._id
        ? await apiClient.patchSecondaryAssumption(assumption._id, {state: 'CANCELLED'}, undefined)
        : await apiClient.patchSecondaryAssumptionByKey(
          {secondaryTherapyId: assumption.secondaryTherapyId, expectedDate: assumption.expectedDate as string},
          {state: 'CANCELLED'},
          undefined
        )
      onAnswered(assumption, updated)
      toast.success('Segnata come non assunta')
    } catch {
      toast.error('Impossibile registrare la risposta. Riprova.')
    } finally {
      setDenyingId(undefined)
    }
  }

  const options = therapyGroups.map((group) => ({value: group.key, label: group.label}))

  const handleFiltersSubmit = (results: FilterResult[]): void => {
    const nextTherapy = results.find((r) => r.property === 'therapy')?.value as string | undefined
    const nextState = results.find((r) => r.property === 'state')?.value as string | undefined
    const dateRange = results.find((r) => r.property === 'date')?.value as [string | null, string | null] | undefined

    // Cleared (X / "Azzera") -> back to every accessory therapy.
    setSelectedGroupKey(nextTherapy || undefined)
    setStateFilter(nextState ?? '')
    setDateFrom(dateRange?.[0] ?? null)
    setDateTo(dateRange?.[1] ?? null)
    setPagination((prev) => ({...prev, page: 0}))
  }

  // Filters follow the PSP-configured columns: no "Stato"/"Data" filter when that column is
  // switched off. "Terapia" always stays — it picks which accessory therapy the table shows.
  const dataColumns = getAssumptionTableColumns(assumptionColumnOptions)
  const hasColumn = (id: string): boolean => dataColumns.some((col) => col.id === id)
  // Each filter is named like the table column it filters (PSP-configured label), so "Farmaci" in
  // the table is "Farmaci" in the panel too — the fallback only applies when that column is off.
  const columnLabel = (id: string, fallback: string): string => dataColumns.find((col) => col.id === id)?.label || fallback

  const filtersConfig: FilterItem[] = [
    // showSearch: typing narrows the list (e.g. "Vol" -> Voltaren) instead of scrolling it.
    {id: 'therapy', label: columnLabel('drug', 'Terapia'), type: 'select', showSearch: true, defaultValue: selectedGroupKey ?? '', options},
    ...(hasColumn('status') ? [{
      id: 'state',
      label: columnLabel('status', 'Stato'),
      type: 'select' as const,
      defaultValue: stateFilter,
      options: [
        {value: '', label: 'Tutti'},
        ...Object.entries(SECONDARY_ASSUMPTION_STATE_LABELS).map(([value, label]) => ({value, label})),
      ],
    }] : []),
    ...(hasColumn('date') ? [{id: 'date', label: columnLabel('date', 'Data'), type: 'rangePicker' as const}] : []),
  ]

  // Data/Ora show when the dose was actually taken once the patient declared it (takenAt, only on
  // COMPLETED), falling back to the planned time — otherwise an answer given with a different time
  // would keep showing the old one.
  const shownDateOf = (row: SecondaryAssumptionRow): string | undefined => row.takenAt ?? row.expectedDate

  const drugNameOf = (assumption: SecondaryAssumption): string =>
    assumption.drugName ?? therapyGroups.find((group) => group.therapyIds.includes(assumption.secondaryTherapyId))?.label ?? '—'

  // Doses still ahead go in the "Prossima assunzione" card, everything else in the table — so
  // every row of the table is a dose whose time has come, with its own date, time and state.
  const isUpcoming = (assumption: SecondaryAssumption): boolean => isUpcomingSecondaryAssumption(assumption, now)
  const upcomingDoses = nextDoses.filter(isUpcoming)
  // While the answered dose is in flight its card row stays as an invisible placeholder, until
  // the refetch brings back the following dose of the same therapy to take its place.
  const placeholder = flight && !upcomingDoses.some((dose) => dose.secondaryTherapyId === flight.source.secondaryTherapyId)
    ? flight.source
    : undefined
  const cardDoses = placeholder
    ? [...upcomingDoses.slice(0, flight!.sourceIndex), placeholder, ...upcomingDoses.slice(flight!.sourceIndex)]
    : upcomingDoses

  const renderDataColumn = ({id, label}: AssumptionTableColumn): ColumnDef<SecondaryAssumptionRow> => {
    switch (id) {
      case 'date':
        return {key: id, header: label, render: (row) => formatDayLabel(shownDateOf(row))}
      case 'time':
        return {key: id, header: label, render: (row) => formatAssumptionTime(shownDateOf(row)) || '—'}
      case 'drug':
        return {key: id, header: label, render: drugNameOf}
      case 'status':
        return {
          key: id,
          header: label,
          render: (row) => <StatusPill label={formatSecondaryAssumptionState(row.state)} color={getSecondaryAssumptionStateTone(row.state)} />,
        }
      default:
        return {key: id, header: label, render: (row) => formatBodyParts(row) || '—'}
    }
  }

  const actionColumns: ColumnDef<SecondaryAssumptionRow>[] = readOnly ? [] : [
    {
      key: 'actions',
      header: '',
      width: 136,
      isActionColumn: true,
      render: (row) => (
        <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{display: 'flex', alignItems: 'center'}}>
          {isSecondaryAssumptionAnswered(row) ? (
            <ActionIconButton variant="edit" tooltip="Modifica" onClick={() => setRespondingTo(row)} />
          ) : (
            <QuickAnswerButtonsNew
              denyLoading={denyingId === getSecondaryAssumptionKey(row)}
              onDeny={() => onQuickDeny(row)}
              onConfirm={() => setConfirmingTaken(row)}
            />
          )}
        </Box>
      ),
    },
  ]
  const tableColumns: ColumnDef<SecondaryAssumptionRow>[] = [...dataColumns.map(renderDataColumn), ...actionColumns]

  // Keyed by getSecondaryAssumptionKey, not `_id`: a derived "next dose" row has `_id: null`, and
  // once several therapies are merged there can be one such row per therapy.
  // Only the rows the card shows are left out. Not "every row still ahead": the Dispatcher persists
  // a dose when it notifies it, NOTIFICATION_ADVANCE_MINUTES before its time, and that row (no
  // longer derived, so not in the card) must show here as "Da confermare" right away.
  const isInCard = (a: SecondaryAssumption): boolean => isDerivedSecondaryAssumption(a) && isUpcoming(a)
  const rows: SecondaryAssumptionRow[] = assumptions
    .filter((a) => !isInCard(a))
    .map((a) => ({...a, rowKey: getSecondaryAssumptionKey(a)}))

  return (
    <Box>
      <Box sx={{mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
        {asNeededButton}
        <Filters key="secondary-assumptions-filters" config={filtersConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {!readOnly && (
        <NextDosesCard
          nextDoses={cardDoses}
          drugNameOf={drugNameOf}
          denyingKey={denyingId}
          placeholderKey={placeholder && getSecondaryAssumptionKey(placeholder)}
          onDeny={(dose) => setCardDialog({dose, presetTaken: 'no'})}
          onConfirm={(dose) => setCardDialog({dose, presetTaken: 'yes'})}
        />
      )}

      <Box ref={tableRef}>
        <DataTable<SecondaryAssumptionRow>
          columns={tableColumns}
          rows={rows}
          keyField="rowKey"
          loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
          emptyMessage="Nessun promemoria inviato per questa terapia."
          // the count includes the derived next doses (RF3.2), which are in the card, not the table
          totalElements={Math.max(0, count - upcomingDoses.length)}
          pagination={pagination}
          onPaginationChange={setPagination}
          mobileActionsPosition="bottom"
        />
      </Box>

      {flightLayer}

      {!readOnly && (
        <>
          <AssumptionResponseDialogNew
            open={Boolean(respondingTo)}
            assumption={respondingTo}
            onClose={() => setRespondingTo(undefined)}
            onSuccess={(updated) => {
              setRespondingTo(undefined)
              if (respondingTo) onAnswered(respondingTo, updated)
            }}
          />

          <NextDoseDialogNew
            open={Boolean(cardDialog)}
            dose={cardDialog?.dose}
            presetTaken={cardDialog?.presetTaken ?? 'yes'}
            onClose={() => setCardDialog(undefined)}
            onAnswered={(updated) => {
              const dose = cardDialog?.dose
              setCardDialog(undefined)
              if (dose) onAnswered(dose, updated)
            }}
            onMoved={() => {
              setCardDialog(undefined)
              fetchAssumptions(true)
            }}
          />

          {asNeededDialog}

          <AssumptionResponseDialogNew
            open={Boolean(confirmingTaken)}
            assumption={confirmingTaken}
            presetTaken="yes"
            onClose={() => setConfirmingTaken(undefined)}
            onSuccess={(updated) => {
              setConfirmingTaken(undefined)
              if (confirmingTaken) onAnswered(confirmingTaken, updated)
            }}
          />
        </>
      )}
    </Box>
  )
}
